import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowLeft,
  Calendar,
  Sun,
  Heart,
  Briefcase,
  Activity,
  Star,
  Crown,
  TrendingUp,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const WeeklyForecastPage = () => {
  const navigate = useNavigate();
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchForecast = async () => {
      try {
        const response = await api.get("/premium/weekly-forecast");
        setForecast(response.data);
      } catch (error) {
        if (error.response?.status === 403) {
          toast.error("Premium subscription required");
          navigate("/premium");
        } else if (error.response?.status === 400) {
          toast.error("Please complete your profile with date of birth");
          navigate("/profile");
        } else {
          console.error("Error fetching forecast:", error);
          toast.error("Failed to load weekly forecast");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchForecast();
  }, [navigate]);

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="text-center space-y-4">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="text-white/70 font-body">Consulting the stars...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="weekly-forecast-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/premium")}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#8B5CF6] to-[#5B21B6] flex items-center justify-center">
                <Calendar className="w-4 h-4 text-white" />
              </div>
              <span className="font-display text-xl text-white">Weekly Forecast</span>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30">
            <Crown className="w-4 h-4 text-[#D4AF37]" />
            <span className="text-xs text-[#D4AF37] font-body">Premium</span>
          </div>
        </div>
      </header>

      {forecast && (
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          {/* Week Header */}
          <div className="text-center animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 mb-4">
              <Sun className="w-4 h-4 text-[#C4B5FD]" />
              <span className="text-sm text-[#C4B5FD] font-body">{forecast.sun_sign}</span>
            </div>
            <h1 className="font-display text-2xl sm:text-3xl text-white mb-2">
              {formatDate(forecast.week_start)} - {formatDate(forecast.week_end)}
            </h1>
            <p className="text-white/60 font-body">Your cosmic guidance for the week ahead</p>
          </div>

          {/* Overall Theme */}
          <div className="gold-accent-card p-6 sm:p-8 text-center animate-fade-in-up" style={{ animationDelay: "0.1s" }}>
            <Sparkles className="w-8 h-8 text-[#D4AF37] mx-auto mb-4" />
            <h2 className="font-display text-2xl sm:text-3xl text-white mb-4">"{forecast.overall_theme}"</h2>
            <p className="text-white/70 font-body leading-relaxed">{forecast.energy_forecast}</p>
          </div>

          {/* Lucky Days */}
          {forecast.lucky_days && forecast.lucky_days.length > 0 && (
            <div className="flex flex-wrap justify-center gap-3 animate-fade-in-up" style={{ animationDelay: "0.15s" }}>
              <span className="text-white/50 font-body text-sm">Lucky Days:</span>
              {forecast.lucky_days.map((day, idx) => (
                <span
                  key={idx}
                  className="px-4 py-2 rounded-full bg-[#D4AF37]/20 border border-[#D4AF37]/40 text-[#D4AF37] text-sm font-body"
                >
                  <Star className="w-3 h-3 inline mr-1" />
                  {day}
                </span>
              ))}
            </div>
          )}

          {/* Opportunities & Challenges */}
          <div className="grid md:grid-cols-2 gap-4 animate-fade-in-up" style={{ animationDelay: "0.2s" }}>
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="font-display text-lg text-white">Opportunities</h3>
              </div>
              <ul className="space-y-3">
                {forecast.opportunities.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-orange-400" />
                </div>
                <h3 className="font-display text-lg text-white">Challenges</h3>
              </div>
              <ul className="space-y-3">
                {forecast.challenges.map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-white/80 font-body text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-orange-400 mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Life Area Forecasts */}
          <div className="space-y-4 animate-fade-in-up" style={{ animationDelay: "0.25s" }}>
            {/* Love */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-pink-500/20 flex items-center justify-center">
                  <Heart className="w-5 h-5 text-pink-400" />
                </div>
                <h3 className="font-display text-lg text-white">Love & Relationships</h3>
              </div>
              <p className="text-white/70 font-body leading-relaxed">{forecast.love_forecast}</p>
            </div>

            {/* Career */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Briefcase className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="font-display text-lg text-white">Career & Finance</h3>
              </div>
              <p className="text-white/70 font-body leading-relaxed">{forecast.career_forecast}</p>
            </div>

            {/* Health */}
            <div className="glass-card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-green-400" />
                </div>
                <h3 className="font-display text-lg text-white">Health & Wellness</h3>
              </div>
              <p className="text-white/70 font-body leading-relaxed">{forecast.health_forecast}</p>
            </div>
          </div>

          {/* Weekly Advice */}
          <div className="gold-accent-card p-6 sm:p-8 animate-fade-in-up" style={{ animationDelay: "0.3s" }}>
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-5 h-5 text-[#D4AF37]" />
              <h3 className="font-display text-xl text-white">Cosmic Advice</h3>
            </div>
            <p className="text-white/80 font-body leading-relaxed text-lg italic">"{forecast.advice}"</p>
          </div>

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-4 pt-4 animate-fade-in-up" style={{ animationDelay: "0.35s" }}>
            <Button
              onClick={() => navigate("/premium/birth-chart")}
              className="btn-secondary flex-1"
            >
              <Star className="w-5 h-5 mr-2" />
              View Birth Chart
            </Button>
            <Button
              onClick={() => navigate("/premium/compatibility")}
              className="btn-gold flex-1"
            >
              <Heart className="w-5 h-5 mr-2" />
              Check Compatibility
            </Button>
          </div>
        </main>
      )}
    </div>
  );
};

export default WeeklyForecastPage;
